import { Button, Card, Flex, Typography } from 'antd';
import dayjs from 'dayjs';
import { CreditCard, Repeat } from 'lucide-react';

import { TextQuaternary, TextTertiary } from '../../../ui/components/text';
import { formatDate } from '../../../ui/utils/format-date';

import { BillingHistory } from './BillingHistory';
import { CreditsCard } from './CreditsCard';
import { PaymentMethodText } from './PaymentMethodText';
import { UsageBreakdown } from './UsageBreakdown';
import { YourPlanCard } from './YourPlanCard';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

export type BillingOverviewProps = {
  organizationId: string;
  overview: GetBillingOverviewResponseDto;
  portalLoading: boolean;
  resubscribeLoading: boolean;
  onManagePlan: () => void;
  onOpenPortal: () => void;
  onResubscribe: () => void;
};

export const BillingOverview = ({
  organizationId,
  overview,
  portalLoading,
  resubscribeLoading,
  onManagePlan,
  onOpenPortal,
  onResubscribe,
}: BillingOverviewProps) => {
  // The window the usage below is summed over: the plan grant's, which a billing-cycle change in
  // Stripe does not move. The subscription's own period is not it.
  const periodStart = overview.usagePeriod?.start;

  // Cancelling, and undoing a scheduled cancellation, both happen in the Stripe portal. A plan that
  // already ended has neither: the plan card offers Re-subscribe.
  const { subscription } = overview;
  const planRow =
    !subscription || !['trialing', 'active', 'past_due'].includes(subscription.status)
      ? null
      : subscription.cancelledAt
        ? {
            note: `Your plan ends ${formatDate(subscription.cancelledAt)}.`,
            action: 'Reactivate subscription',
            testId: 'billing-reactivate-subscription',
          }
        : {
            note:
              subscription.status === 'trialing'
                ? `Cancel before ${formatDate(subscription.trialEndsAt)} and your card is not charged.`
                : 'Cancelling takes effect at the end of your billing period.',
            action: 'Cancel subscription',
            testId: 'billing-cancel-subscription',
          };

  return (
    <Flex vertical gap={28}>
      <Flex vertical gap={10}>
        <Typography.Text strong style={{ fontSize: 15 }}>
          Your plan
        </Typography.Text>
        <YourPlanCard
          overview={overview}
          portalLoading={portalLoading}
          resubscribeLoading={resubscribeLoading}
          onManagePlan={onManagePlan}
          onOpenPortal={onOpenPortal}
          onResubscribe={onResubscribe}
        />
      </Flex>

      <Flex vertical gap={10}>
        <Typography.Text strong style={{ fontSize: 15 }}>
          Your credits
        </Typography.Text>
        <CreditsCard
          organizationId={organizationId}
          overview={overview}
          portalLoading={portalLoading}
          onOpenPortal={onOpenPortal}
        />
      </Flex>

      <Flex vertical gap={10}>
        <Flex align="center" justify="space-between" gap={8}>
          <Typography.Text strong style={{ fontSize: 15 }}>
            Where credits went
          </Typography.Text>
          {periodStart && (
            <TextQuaternary style={{ fontSize: 12 }}>
              Since {dayjs(periodStart).format('MMM D')}
            </TextQuaternary>
          )}
        </Flex>
        <UsageBreakdown items={overview.usageThisPeriod} />
      </Flex>

      <Flex vertical gap={10}>
        <Typography.Text strong style={{ fontSize: 15 }}>
          Billing history
        </Typography.Text>
        <BillingHistory
          organizationId={organizationId}
          portalLoading={portalLoading}
          onOpenPortal={onOpenPortal}
        />
      </Flex>

      <Flex vertical gap={10}>
        <Typography.Text strong style={{ fontSize: 15 }}>
          Payment &amp; invoices
        </Typography.Text>
        <Card style={{ borderRadius: 20, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
          <Flex align="center" gap={14} style={{ padding: '13px 18px' }}>
            <CreditCard size={15} color="var(--ant-color-text-tertiary)" />
            <Flex vertical gap={2} flex={1}>
              <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
                {overview.paymentMethod ? (
                  <PaymentMethodText method={overview.paymentMethod} />
                ) : (
                  'No card on file'
                )}
              </Typography.Text>
            </Flex>
            <Button
              size="small"
              loading={portalLoading}
              onClick={onOpenPortal}
              style={{
                flex: 'none',
                height: 'auto',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {overview.paymentMethod ? 'Change' : 'Add card'}
            </Button>
          </Flex>
          {planRow && (
            <Flex
              align="center"
              gap={14}
              style={{ padding: '13px 18px', borderTop: '1px solid var(--ant-color-split)' }}
            >
              <Repeat size={15} color="var(--ant-color-text-tertiary)" />
              <Flex vertical gap={2} flex={1}>
                <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>Subscription</Typography.Text>
                <TextTertiary style={{ fontSize: 12 }}>{planRow.note}</TextTertiary>
              </Flex>
              <Button
                size="small"
                loading={portalLoading}
                onClick={onOpenPortal}
                data-testid={planRow.testId}
                style={{
                  flex: 'none',
                  height: 'auto',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {planRow.action}
              </Button>
            </Flex>
          )}
        </Card>
      </Flex>
    </Flex>
  );
};
